import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAnalytics, usePageTracking } from '../hooks/useAnalytics';
import {
  ApplicantMarket,
  ApplicantV2Session,
  confirmDocumentUpload,
  createApplicantV2,
  getApplicantMarkets,
  getApplicantV2ErrorMessage,
  requestDocumentUpload,
  saveApplicantV2Session,
} from '../utils/applicantV2';

const deliveryDays = [
  ['MON', 'M'], ['TUE', 'T'], ['WED', 'W'], ['THU', 'T'],
  ['FRI', 'F'], ['SAT', 'S'], ['SUN', 'S'],
];

const deliveryTimes = [
  ['morning', 'Morning'],
  ['afternoon', 'Afternoon'],
  ['evening', 'Evening'],
  ['lateNight', 'Late Night'],
];

const vehicleTypes = ['Car', 'Truck', 'EV'];
const referralSources = ['Social Media', 'Indeed/Job Sites', 'Friend/Family Referral', 'Google Search', 'Other'];

interface ApplyFormState {
  name: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  city: string;
  startDate: string;
  hasInsurance: boolean;
  vehicleType: string;
  residentialStreet: string;
  residentialCity: string;
  residentialProvince: string;
  residentialPostalCode: string;
  referralSource: string;
}

interface ApplyProps {
  embedded?: boolean;
}

const initialFormState: ApplyFormState = {
  name: '', email: '', phone: '', dateOfBirth: '', city: '', startDate: '',
  hasInsurance: false, vehicleType: '', residentialStreet: '', residentialCity: '',
  residentialProvince: 'Ontario', residentialPostalCode: '', referralSource: '',
};

const getOldestEligibleBirthDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 16);
  return date.toISOString().slice(0, 10);
};

const withInferredDocumentType = (file: File) => {
  if (file.type) return file;
  const extension = file.name.split('.').pop()?.toLowerCase();
  const type = extension === 'pdf' ? 'application/pdf' : extension === 'png' ? 'image/png' : 'image/jpeg';
  return new File([file], file.name, { type });
};

const Apply: React.FC<ApplyProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState<ApplyFormState>(initialFormState);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [createdSession, setCreatedSession] = useState<ApplicantV2Session | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [markets, setMarkets] = useState<ApplicantMarket[]>([]);
  const [marketsLoading, setMarketsLoading] = useState(true);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [error, setError] = useState('');
  const oldestEligibleBirthDate = useMemo(getOldestEligibleBirthDate, []);

  usePageTracking(embedded ? 'Home Application Form' : 'Apply');
  const { trackApplicationStarted, trackAPIError } = useAnalytics();

  useEffect(() => {
    getApplicantMarkets()
      .then(setMarkets)
      .catch(() => setError('Unable to load delivery markets. Please refresh and try again.'))
      .finally(() => setMarketsLoading(false));
  }, []);

  const updateField = (field: keyof ApplyFormState, value: string | boolean) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const toggleValue = (value: string, values: string[], setValues: React.Dispatch<React.SetStateAction<string[]>>) => {
    setValues(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
  };

  const matchResidentialCityToMarket = () => {
    const normalizedCity = form.residentialCity.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    const match = markets.find((market) =>
      market.label.toLowerCase().replace(/[^a-z0-9]/g, '').includes(normalizedCity)
    );
    if (normalizedCity && match) updateField('city', match.value);
  };

  const handleLicenseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file && file.size > 8 * 1024 * 1024) {
      event.target.value = '';
      setLicenseFile(null);
      setError('Driver’s licence must be 8MB or smaller.');
      return;
    }
    setLicenseFile(file);
    setError('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!licenseFile) return setError('A driver’s licence is required.');
    if (!selectedDays.length || !selectedTimes.length) return setError('Select at least one available day and time.');
    if (!consentAccepted) return setError('Consent to SMS messages and application data processing is required.');

    setIsSubmitting(true);
    setError('');
    const normalizedEmail = form.email.trim().toLowerCase();
    let applicationSaved = Boolean(createdSession);

    try {
      let session = createdSession;
      if (!session) {
        session = await createApplicantV2({
          fullName: form.name.trim(), email: normalizedEmail, phone: form.phone.trim(),
          dateOfBirth: form.dateOfBirth, deliveryCity: form.city, vehicleType: form.vehicleType,
          startDate: form.startDate, hasInsurance: form.hasInsurance,
          residentialStreet: form.residentialStreet.trim(), residentialCity: form.residentialCity.trim(),
          residentialProvince: form.residentialProvince.trim(), residentialPostalCode: form.residentialPostalCode.trim(),
          availableDays: selectedDays, availableTimes: selectedTimes, referralSource: form.referralSource.trim(),
          consent: { sms: true, dataProcessing: true }, applicationSource: 'driverportal-applicant-v2',
        });
        saveApplicantV2Session(session);
        setCreatedSession(session);
        applicationSaved = true;
      }

      const uploadFile = withInferredDocumentType(licenseFile);
      const upload = await requestDocumentUpload(session, 'drivers-license', uploadFile);
      const uploadResponse = await fetch(upload.uploadUrl, {
        method: 'PUT', headers: { 'Content-Type': uploadFile.type }, body: uploadFile,
      });
      if (!uploadResponse.ok) throw new Error('Driver’s licence upload failed.');
      await confirmDocumentUpload(session, 'drivers-license', upload.documentKey);

      trackApplicationStarted({ email: normalizedEmail, applicantId: session.applicationId, entrySource: 'new_application' });
      navigate('/wizard/fit-assessment');
    } catch (err: any) {
      trackAPIError({
        endpoint: '/v2/applications', errorCode: err.response?.status || 'unknown', errorMessage: err.message,
      });
      setError(getApplicantV2ErrorMessage(
        err,
        err.response?.status === 409
          ? 'An application already exists. Use your phone number above to continue.'
          : applicationSaved
            ? 'Your application was saved, but the driver’s licence upload failed. Please retry the upload.'
          : 'Your application or driver’s licence could not be submitted. Please check the form and try again.'
      ));
      setIsSubmitting(false);
    }
  };

  const formCard = (
    <div className="application-card start-form-card">
      <div className="card-introduction">
        <h1>Start Your Application</h1>
        <p>Takes about five minutes. All fields marked * are required.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <h2>Personal Information</h2>
        <div className="form-grid">
          <label>Full Name *<input required value={form.name} onChange={(event) => updateField('name', event.target.value)} /></label>
          <label>Email Address *<input required type="email" value={form.email} onChange={(event) => updateField('email', event.target.value)} /></label>
          <label>Phone Number *<input required type="tel" value={form.phone} onChange={(event) => updateField('phone', event.target.value)} /></label>
          <label>Date of Birth *
            <input required type="date" min="1900-01-01" max={oldestEligibleBirthDate} value={form.dateOfBirth} onChange={(event) => updateField('dateOfBirth', event.target.value)} />
            <span className="field-help">Applicants must be at least 16 years old.</span>
          </label>
          <label className="form-grid-wide">Driver’s Licence *
            <span className="field-help">PDF, JPG, or PNG. Maximum 8MB.</span>
            <input required type="file" accept=".pdf,image/jpeg,image/png" onChange={handleLicenseChange} />
          </label>
        </div>

        <h2>Delivery Information</h2>
        <div className="form-grid">
          <label>Delivery City *
            <select required disabled={marketsLoading || !markets.length} value={form.city} onChange={(event) => updateField('city', event.target.value)}>
              <option value="">{marketsLoading ? 'Loading active markets…' : 'Select city'}</option>
              {markets.map((market) => <option key={market.value} value={market.value}>{market.label}</option>)}
            </select>
          </label>
          <label>Vehicle Type *
            <select required value={form.vehicleType} onChange={(event) => updateField('vehicleType', event.target.value)}>
              <option value="">Select type</option>
              {vehicleTypes.map((vehicle) => <option key={vehicle}>{vehicle}</option>)}
            </select>
          </label>
          <label>Where did you hear about us?
            <select value={form.referralSource} onChange={(event) => updateField('referralSource', event.target.value)}>
              <option value="">Please select</option>
              {referralSources.map((source) => <option key={source}>{source}</option>)}
            </select>
          </label>
        </div>
        <label className="check-row"><input type="checkbox" checked={form.hasInsurance} onChange={(event) => updateField('hasInsurance', event.target.checked)} />I have valid vehicle insurance</label>

        <h2>Residential Address</h2>
        <p className="form-section-note">Required for applicant verification and shipping your courier kit.</p>
        <div className="form-grid">
          <label className="form-grid-wide">Street Address *<input required autoComplete="street-address" value={form.residentialStreet} onChange={(event) => updateField('residentialStreet', event.target.value)} /></label>
          <label>City *
            <input required autoComplete="address-level2" value={form.residentialCity} onChange={(event) => updateField('residentialCity', event.target.value)} onBlur={matchResidentialCityToMarket} />
            <span className="field-help">If this matches an active market, Delivery City is selected automatically.</span>
          </label>
          <label>Province *
            <select required autoComplete="address-level1" value={form.residentialProvince} onChange={(event) => updateField('residentialProvince', event.target.value)}><option value="Ontario">Ontario</option></select>
          </label>
          <label>Postal Code *<input required autoComplete="postal-code" pattern="[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d" placeholder="A1A 1A1" value={form.residentialPostalCode} onChange={(event) => updateField('residentialPostalCode', event.target.value)} /></label>
        </div>

        <h2>Availability</h2>
        <span className="group-label">Available Days *</span>
        <div className="choice-row">
          {deliveryDays.map(([value, label]) => <button key={value} type="button" className={selectedDays.includes(value) ? 'selected' : ''} onClick={() => toggleValue(value, selectedDays, setSelectedDays)} aria-pressed={selectedDays.includes(value)}>{label}</button>)}
        </div>
        <span className="group-label">Available Times *</span>
        <div className="choice-row choice-row--times">
          {deliveryTimes.map(([value, label]) => <button key={value} type="button" className={selectedTimes.includes(value) ? 'selected' : ''} onClick={() => toggleValue(value, selectedTimes, setSelectedTimes)} aria-pressed={selectedTimes.includes(value)}>{label}</button>)}
        </div>
        <div className="form-grid"><label>Start Date *<input required type="date" value={form.startDate} onChange={(event) => updateField('startDate', event.target.value)} /></label></div>

        <label className="check-row consent-row">
          <input required type="checkbox" checked={consentAccepted} onChange={(event) => setConsentAccepted(event.target.checked)} />
          I consent to application-related SMS messages and processing of my application data.
        </label>
        {error && <div className="preview-message" role="alert">{error}</div>}
        <button className="primary-button" disabled={isSubmitting || marketsLoading || !licenseFile || !selectedDays.length || !selectedTimes.length} type="submit">
          {isSubmitting ? 'Submitting…' : 'Submit Application →'}
        </button>
      </form>
    </div>
  );

  if (embedded) return formCard;

  return (
    <div className="app-shell">
      <Header />
      <main className="page-content standalone-application"><section className="landing-section">{formCard}</section></main>
      <Footer />
    </div>
  );
};

export default Apply;
